import React, { useState, useEffect, useContext, Fragment } from "react"
import getPercentage from "utilities/getPercentage"
import { Flex } from "antd"

// components
import CTGGroupTotals from "../CTGGroupTotals"
import { sanitizeValue } from "../../../../../../utils"

// context
import { AEContext } from "../AdversePreview"
import "./AdverseEvents.scss"

const AdverseEvents = ({ title, type, totalType, statSchema, bgColor }) => {
  const { data, view, schema } = useContext(AEContext)
  const [events, setEvents] = useState([])
  const isSeriousAE = type === "serious_adverse_events"

  useEffect(() => {
    if (data?.[type]) {
      let map = new Map()

      data[type].forEach((event) => {
        if (map.has(event.organ_system_name)) {
          let eventsArr = map.get(event.organ_system_name)
          eventsArr.push({ ...event })
          map.set(event.organ_system_name, eventsArr)
        } else {
          map.set(event.organ_system_name, [{ ...event }])
        }
      })

      let events = []
      map.forEach((value, key) => {
        events.push({
          title: key,
          rows: value,
        })
      })
      setEvents(events)
    }
  }, [data?.[type]])

  if (view === "eupas") {
    return null
  }

  const renderValue = (value) => {
    return value ?? "-"
  }

  if (view === "eudract" && schema.properties?.[view]) {
    const renderCell = (cell) => {
      if (type === "non_serious_adverse_events") {
        return (
          <Flex
            vertical
            gap={8}
          >
            <Flex>
              {renderValue(cell.num_subjects_affected)} / {renderValue(cell.num_subjects)} (
              {getPercentage(cell.num_subjects_affected, cell.num_subjects)})
            </Flex>
            <Flex>{renderValue(cell.num_events)}</Flex>
          </Flex>
        )
      } else {
        return (
          <Flex
            vertical
            gap={8}
          >
            <Flex>
              {renderValue(cell.num_subjects_affected)} / {renderValue(cell.num_subjects)} (
              {getPercentage(cell.num_subjects_affected, cell.num_subjects)})
            </Flex>
            <Flex>
              {renderValue(cell.occurences_causally_related_to_treatment)} / {renderValue(cell.num_events)} (
              {getPercentage(cell.occurences_causally_related_to_treatment, cell.num_events)})
            </Flex>
            <Flex>
              {renderValue(cell.deaths_causally_related_to_treatment) ?? "NA"} / {renderValue(cell.deaths)} (
              {getPercentage(cell.deaths_causally_related_to_treatment, cell.deaths)})
            </Flex>
          </Flex>
        )
      }
    }

    const getAffectedAndTotalInCol = (index) => {
      let affected = 0
      let total = 0

      data.non_serious_adverse_events.map((event) => {
        if (!isSeriousAE) affected += event.values?.[index]?.num_subjects_affected || 0
        total += event.values?.[index]?.num_subjects || 0
      })

      data.serious_adverse_events.map((event) => {
        if (isSeriousAE) affected += event.values?.[index]?.num_subjects_affected || 0
        total += event.values?.[index]?.num_subjects || 0
      })

      return { affected, total }
    }

    const aeHeaderStyle = { fontSize: 10 }

    return (
      <div className="adverse-preview-table adverse-events-preview-container">
        <table className="authoring-table-component">
          <tbody>
            <tr>
              <td
                style={{ fontWeight: "bold" }}
                colSpan={(data?.groups?.length || 0) + 2}
              >
                {title}
              </td>
            </tr>
            <tr>
              <td
                style={{ backgroundColor: "#e2cdfc", fontWeight: "bold" }}
                colSpan={2}
              >
                Groups
              </td>
              {data?.groups?.map((group, i) => {
                return (
                  <td key={`${group.title}-${i}`}>
                    <div style={{ fontWeight: "bold" }}>{group.title}</div>
                  </td>
                )
              })}
            </tr>

            {events.map((event, i) => (
              <Fragment key={`${type}-${event.title}-${i}`}>
                <tr>
                  <td
                    colSpan={2}
                    className="event-header"
                  >
                    <p className="event-header-title">{event.title}</p>
                    <Flex
                      vertical
                      gap={12}
                    >
                      <Flex style={aeHeaderStyle}>Subjects affected / exposed</Flex>
                      {isSeriousAE && <Flex style={aeHeaderStyle}>Number of deaths (all causes)</Flex>}
                      {isSeriousAE && <Flex style={aeHeaderStyle}>Number of deaths resulting from adverse events</Flex>}
                    </Flex>
                  </td>
                  {data?.groups?.map((group, j) => {
                    const { affected, total } = getAffectedAndTotalInCol(j)

                    return (
                      <td className="vertical-bottom">
                        <Flex
                          vertical
                          gap={8}
                        >
                          <Flex>{`${affected}/${total} (${getPercentage(affected, total)})`}</Flex>
                          {isSeriousAE && <Flex>{group.num_deaths}</Flex>}
                          {isSeriousAE && <Flex>{group.deaths_resulting_from_adverse_events}</Flex>}
                        </Flex>
                      </td>
                    )
                  })}
                </tr>
                {event.rows.map((row, j) => (
                  <tr key={`${type}-${event.title}-${i}-${j}`}>
                    <td
                      style={{ backgroundColor: bgColor, textAlign: "left" }}
                      colSpan={2}
                    >
                      <Flex
                        vertical
                        gap={16}
                      >
                        <Flex vertical>
                          <Flex gap={4}>
                            <p className="font-bold">Term: </p> <p>{row.term}</p>
                          </Flex>
                          <Flex gap={4}>
                            <p className="font-bold">Description: </p> <p>{row.notes}</p>
                          </Flex>
                        </Flex>

                        <Flex
                          vertical
                          gap={12}
                        >
                          <Flex style={aeHeaderStyle}>Subjects affected / exposed</Flex>
                          <Flex style={aeHeaderStyle}>
                            {isSeriousAE ? "Occurrences causally related to treatment / all" : "Occurrences (all)"}
                          </Flex>
                          {isSeriousAE && (
                            <Flex style={aeHeaderStyle}>Deaths causally related to treatment / all </Flex>
                          )}
                        </Flex>
                      </Flex>
                    </td>
                    {row.values.map((cell, k) => {
                      return (
                        <td
                          key={`${type}-${event.title}-${i}-${j}-${k}`}
                          className="vertical-bottom"
                        >
                          {renderCell(cell)}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className="adverse-events-preview-container">
      <table className="adverse-preview-table authoring-table-component">
        <tbody>
          <tr>
            <td
              style={{ fontWeight: "bold" }}
              colSpan={(data?.groups?.length || 0) + 2}
            >
              {title}
            </td>
          </tr>
          {view === "ctg" && (
            <CTGGroupTotals
              type={totalType}
              bgColor={bgColor}
            />
          )}
          {events.map((event, i) => (
            <Fragment key={`${type}-${event.title}-${i}`}>
              <tr>
                <td
                  style={{ backgroundColor: "#e2cdfc", fontWeight: "bold" }}
                  colSpan={data.groups.length + 2}
                >
                  {event.title}
                </td>
              </tr>
              {event.rows.map((row, j) => (
                <tr key={`${type}-${event.title}-${i}-${j}`}>
                  <td
                    style={{ backgroundColor: bgColor, textAlign: "right" }}
                    colSpan={2}
                  >
                    {row.term}
                  </td>
                  {row.values.map((cell, k) => {
                    let percentage = 0
                    if (cell.num_subjects_affected && cell.num_subjects) {
                      percentage = (Number(cell.num_subjects_affected) / Number(cell.num_subjects)) * 100
                    }
                    let value = `${sanitizeValue(cell.num_subjects_affected)} / ${sanitizeValue(
                      cell.num_subjects
                    )} (${percentage.toFixed(2)}%)`
                    return <td key={`${type}-${event.title}-${i}-${j}-${k}`}>{value}</td>
                  })}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default AdverseEvents
